import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { deleteUserAccount, updateUserProfile } from '../../services/profileService';
import PersonalInfo from '../../components/profile/PersonalInfo';
import CreditStatus from '../../components/profile/CreditStatus';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const Profile = () => {
  const { user } = useAuth();
  const { profile, loading, error } = useUserProfile(user?.uid);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleUpdateProfile = async (field: string, value: string) => {
    if (!user || !profile) return;
    
    try {
      await updateUserProfile(user.uid, { [field]: value });
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    try {
      await deleteUserAccount(user.uid);
      // La redirection sera gérée par le contexte d'authentification
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setDeleteError('Une erreur est survenue lors de la suppression du compte');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-primary mb-8">Mon profil</h1>
      
      <div className="space-y-8">
        {/* Informations personnelles */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-6">Informations personnelles</h2>
          <PersonalInfo 
            profile={profile || {}} 
            onUpdate={handleUpdateProfile}
          />
        </div>

        {/* Statut des crédits */}
        <CreditStatus credits={profile?.credits} />

        {/* Suppression du compte */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Zone dangereuse</h2>
          <p className="text-gray-600 mb-4">
            La suppression de votre compte est irréversible. Toutes vos données et estimations seront définitivement perdues.
          </p>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn bg-red-600 text-white hover:bg-red-700"
          >
            Supprimer mon compte
          </button>
          {deleteError && (
            <p className="mt-2 text-sm text-red-600">{deleteError}</p>
          )}
        </div>
      </div>

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Supprimer votre compte ?"
        message="Cette action est irréversible. Toutes vos données, y compris vos estimations et crédits, seront définitivement supprimées. Êtes-vous sûr de vouloir continuer ?"
        confirmLabel="Supprimer définitivement"
        cancelLabel="Annuler"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  );
};

export default Profile;
